import {ChevronDownIcon} from '@primer/octicons-react'
import type {
  CompletedReview,
  PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import styles from './Reviewers.module.css'
import {pullRequestsLayoutRoute} from '../../../routes/layout-route'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Banner} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import {useCallback, useRef, useState, type RefObject} from 'react'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {isAutomatedReviewBot, reviewerDisplayName} from './ReviewerIdentity'
import {computeTeamCompositeStates, type TeamCompositeState} from './team-review-state-helpers'
import {useReviewers} from '../data/use-reviewers'
import {announce} from '@github-ui/aria-live'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'
import {isCopilotPullRequestReviewer, isCopilotReviewer, matchesCopilotReviewerRow} from './copilot-reviewer'
import {useReviewersSectionAnchorRef} from './ReviewersSection'
import {CopilotRow} from './rows/CopilotRow'
import {CompletedReviewRow} from './rows/CompletedReviewRow'
import {PendingReviewRow} from './rows/PendingReviewRow'
import {TeamCompositeRow} from './rows/TeamCompositeRow'

export function Reviewers() {
  const returnFocusRef = useReviewersSectionAnchorRef()
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)

  return <ReviewersComponent key={`${owner}/${repo}/${pr_number}`} returnFocusRef={returnFocusRef} />
}

export function ReviewersComponent({
  returnFocusRef,
}: {
  returnFocusRef?: RefObject<HTMLButtonElement | null> | null
} = {}) {
  const {data: reviewers, isPending, isError} = useReviewers()
  const [errorMessage, setErrorMessage] = useState<string>()
  const errorBannerRef = useRef<HTMLElement>(null)
  const restoreFocus = useCallback(() => {
    requestAnimationFrame(() => {
      if (document.activeElement === document.body) returnFocusRef?.current?.focus()
    })
  }, [returnFocusRef])

  if (isPending && !reviewers) return <UserSectionSkeleton placeholderRows={2} />
  if (isError && !reviewers) return <span className={styles.emptyState}>Unable to load reviewers</span>

  const allCompletedReviews = reviewers?.completedReviews ?? []
  const allPendingRequests = reviewers?.pendingReviewRequests ?? []
  const copilotReviewerRow = reviewers?.copilotReviewerRow

  const teamStates = reviewers ? computeTeamCompositeStates(reviewers) : []
  const coveredTeamNames = new Set(teamStates.map(team => team.teamName))
  const completedReviews = allCompletedReviews.filter(
    review => !review.onBehalfOfTeams.some(team => coveredTeamNames.has(team.displayLogin)),
  )
  const pendingRequests = allPendingRequests.filter(request => {
    if (request.reviewer?.reviewerType === 'Team' && coveredTeamNames.has(request.reviewer.displayLogin)) return false
    if (request.delegatedFromTeam && coveredTeamNames.has(request.delegatedFromTeam.displayLogin)) return false
    return true
  })

  const awaitingInitialCopilotRequest =
    !!copilotReviewerRow?.requestable &&
    !allPendingRequests.some(request => matchesCopilotReviewerRow(request.reviewer, copilotReviewerRow)) &&
    !allCompletedReviews.some(review => matchesCopilotReviewerRow(review.reviewer, copilotReviewerRow))

  if (
    teamStates.length === 0 &&
    completedReviews.length === 0 &&
    pendingRequests.length === 0 &&
    !awaitingInitialCopilotRequest
  ) {
    return <span className={styles.emptyState}>No reviewers</span>
  }

  const writeAccessReviews = completedReviews.filter(review => review.hasWriteAccess || isBotReviewer(review.reviewer))
  const nonWriteAccessReviews = completedReviews.filter(
    review => !review.hasWriteAccess && !isBotReviewer(review.reviewer),
  )
  const copilotPendingRequest = pendingRequests.find(
    request =>
      isCopilotPullRequestReviewer(request.reviewer) && matchesCopilotReviewerRow(request.reviewer, copilotReviewerRow),
  )
  const visibleWriteAccessReviews = writeAccessReviews.filter(
    review => !copilotPendingRequest || !matchesCopilotReviewerRow(review.reviewer, copilotReviewerRow),
  )
  const copilotCompletedReview = visibleWriteAccessReviews.find(review =>
    matchesCopilotReviewerRow(review.reviewer, copilotReviewerRow),
  )

  // Grouped and alphabetized per the reviewers section spec. https://github.com/github/pull-requests/issues/25675
  const sortedTeamStates = teamStates.toSorted((a: TeamCompositeState, b: TeamCompositeState) =>
    a.teamName.localeCompare(b.teamName),
  )
  const otherCompletedReviews = visibleWriteAccessReviews.filter(review => review !== copilotCompletedReview)
  const otherPendingRequests = pendingRequests.filter(request => request !== copilotPendingRequest)
  const rootUserRows: UserRow[] = [
    ...otherCompletedReviews.map(review => ({kind: 'completed', review}) as const),
    ...otherPendingRequests.map(request => ({kind: 'pending', request}) as const),
  ]
  rootUserRows.sort((a, b) => userRowSortName(a).localeCompare(userRowSortName(b)))

  const clearMutationError = () => {
    setErrorMessage(undefined)
  }
  const focusErrorBanner = (origin: Element | null) => {
    requestAnimationFrame(() => {
      const active = document.activeElement
      if (active === document.body || active === origin) {
        errorBannerRef.current?.focus()
      }
    })
  }
  const mutationSuccess = (message: string) => {
    announce(message)
    restoreFocus()
  }
  const copilotRequestMutationError = (origin: Element | null) => {
    setErrorMessage('Failed to request review from Copilot.')
    focusErrorBanner(origin)
  }
  const reviewerMutationError = (reviewerName: string | undefined) => (origin: Element | null) => {
    setErrorMessage(reviewerName ? `Failed to re-request review from ${reviewerName}.` : 'Failed to re-request review.')
    focusErrorBanner(origin)
  }
  const reviewerMutationSuccess = (reviewerName: string | undefined) => () =>
    mutationSuccess(`Review requested from ${reviewerName ?? 'reviewer'}`)

  return (
    <div>
      <ul className={styles.reviewerList}>
        <CopilotRow
          copilotPendingRequest={copilotPendingRequest}
          copilotCompletedReview={copilotCompletedReview}
          copilotReviewerRow={copilotReviewerRow}
          awaitingInitialCopilotRequest={awaitingInitialCopilotRequest}
          onClearMutationError={clearMutationError}
          onCopilotRequestMutationError={copilotRequestMutationError}
          onReviewerMutationError={reviewerMutationError}
          onReviewerMutationSuccess={reviewerMutationSuccess}
          onRestoreFocus={restoreFocus}
          onMutationSuccess={mutationSuccess}
        />
        {sortedTeamStates.map(team => (
          <TeamCompositeRow
            key={`team-${team.teamName}`}
            team={team}
            onMutationStart={clearMutationError}
            onMutationError={reviewerMutationError}
            onMutationSuccess={reviewerMutationSuccess}
          />
        ))}
        {rootUserRows.map(row => {
          if (row.kind === 'completed') {
            const review = row.review
            const reviewerName = review.reviewer ? reviewerDisplayName(review.reviewer) : 'reviewer'
            return (
              <CompletedReviewRow
                key={`completed-${review.id}`}
                review={review}
                muted={isBotReviewer(review.reviewer)}
                onMutationStart={clearMutationError}
                onMutationError={reviewerMutationError(reviewerName)}
                onMutationSuccess={reviewerMutationSuccess(reviewerName)}
                onCloseWithoutTarget={restoreFocus}
              />
            )
          }
          const request = row.request
          return <PendingReviewRow key={`pending-${request.reviewer?.id}`} request={request} />
        })}
      </ul>
      {errorMessage ? (
        <Banner ref={errorBannerRef} variant="critical" className={styles.mutationError} role="alert">
          <Banner.Title as="h5">{errorMessage}</Banner.Title>
        </Banner>
      ) : null}
      {nonWriteAccessReviews.length > 0 && (
        <NonWriteAccessReviewers
          reviews={nonWriteAccessReviews}
          hasWriteAccessReviews={visibleWriteAccessReviews.length > 0}
          onMutationStart={clearMutationError}
          onMutationError={reviewerMutationError}
          onMutationSuccess={reviewerMutationSuccess}
          onCloseWithoutTarget={restoreFocus}
        />
      )}
    </div>
  )
}

function isBotReviewer(reviewer: CompletedReview['reviewer']): boolean {
  return isCopilotReviewer(reviewer) || isAutomatedReviewBot(reviewer)
}

function NonWriteAccessReviewers({
  reviews,
  hasWriteAccessReviews,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
  onCloseWithoutTarget,
}: {
  reviews: CompletedReview[]
  hasWriteAccessReviews: boolean
  onMutationStart: () => void
  onMutationError: (reviewerName: string | undefined) => (origin: Element | null) => void
  onMutationSuccess: (reviewerName: string | undefined) => () => void
  onCloseWithoutTarget?: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(true)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const toggle = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.reviewers_toggle', 'OVERVIEW_REVIEWER', {is_open: !isExpanded})
    setIsExpanded(previous => !previous)
  }, [isExpanded, sendPullRequestAnalyticsEvent])

  const count = reviews.length
  const label = `${hasWriteAccessReviews ? '+' : ''}${count} more ${count === 1 ? 'reviewer' : 'reviewers'}`

  return (
    <div className={styles.nonWriteAccessGroup}>
      <Tooltip text="Reviewers whose approvals may not affect merge requirements">
        <button type="button" className={styles.moreReviewersToggle} aria-expanded={isExpanded} onClick={toggle}>
          <span>{label}</span>
          <ChevronDownIcon
            size={16}
            className={clsx(styles.moreReviewersChevron, isExpanded && styles.moreReviewersChevronExpanded)}
          />
        </button>
      </Tooltip>
      {isExpanded && (
        <ul className={clsx(styles.reviewerList, 'pl-3')}>
          {reviews.map(review => (
            <CompletedReviewRow
              key={`completed-${review.id}`}
              review={review}
              muted
              onMutationStart={onMutationStart}
              onMutationError={onMutationError(review.reviewer ? reviewerDisplayName(review.reviewer) : undefined)}
              onMutationSuccess={onMutationSuccess(review.reviewer ? reviewerDisplayName(review.reviewer) : undefined)}
              onCloseWithoutTarget={onCloseWithoutTarget}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

type UserRow = {kind: 'completed'; review: CompletedReview} | {kind: 'pending'; request: PendingReviewRequest}

function userRowSortName(row: UserRow): string {
  const reviewer = row.kind === 'completed' ? row.review.reviewer : row.request.reviewer
  return reviewer ? reviewerDisplayName(reviewer) : ''
}
